import React, { useContext, useState, useEffect } from "react";
import {
  Container,
  TextField,
  Grid,
  Typography,
  Paper,
  Button,
  Snackbar,
  Alert,
  Card,
  CardContent,
  Box,
  MenuItem,
} from "@mui/material";

import { motion } from "framer-motion";
import axios from "axios";
import { useNavigate } from "react-router-dom";

import { api } from "@api/api";
import estadosMunicipios from "./estados-municipios.json";
import { CondicaoContext } from "../../../contexto";
import { ajustarTamanhoFonteEscola } from "../../../estilos/global";

const maskCNPJ = (value) => {
  if (!value) return "";

  value = value.replace(/\D/g, "");

  if (value.length <= 2) return value;

  if (value.length <= 5) {
    return `${value.substring(0, 2)}.${value.substring(2)}`;
  }

  if (value.length <= 8) {
    return `${value.substring(0, 2)}.${value.substring(2, 5)}.${value.substring(5)}`;
  }

  if (value.length <= 12) {
    return `${value.substring(0, 2)}.${value.substring(2, 5)}.${value.substring(5, 8)}/${value.substring(8)}`;
  }

  return `${value.substring(0, 2)}.${value.substring(2, 5)}.${value.substring(5, 8)}/${value.substring(8, 12)}-${value.substring(12, 14)}`;
};

const maskPhone = (value) => {
  if (!value) return "";

  value = value.replace(/\D/g, "");

  if (value.length === 0) return "";

  if (value.length > 10) {
    return `(${value.substring(0, 2)}) ${value.substring(2, 7)}-${value.substring(7, 11)}`;
  }

  if (value.length > 6) {
    return `(${value.substring(0, 2)}) ${value.substring(2, 6)}-${value.substring(6, 10)}`;
  }

  if (value.length > 2) {
    return `(${value.substring(0, 2)}) ${value.substring(2)}`;
  }

  return `(${value}`;
};

const maskCEP = (value) => {
  if (!value) return "";

  value = value.replace(/\D/g, "");

  if (value.length <= 5) return value;

  return `${value.substring(0, 5)}-${value.substring(5, 8)}`;
};

export default function RegistroMantenedora({ onNext }) {
  const [nomeMantenedora, setNomeMantenedora] = useState("");
  const [cnpj, setCnpj] = useState("");
  const [rua, setRua] = useState("");
  const [bairro, setBairro] = useState("");
  const [numero, setNumero] = useState("");
  const [cidade, setCidade] = useState("");
  const [estado, setEstado] = useState("");
  const [cep, setCep] = useState("");
  const [email, setEmail] = useState("");
  const [telefone, setTelefone] = useState("");
  const [documento, setDocumento] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [showRegistro, setShowRegistro] = useState(false);

  const navigate = useNavigate();

  const { setMantenedoraRegistrada } = useContext(CondicaoContext);

  const estadoSelecionado = estadosMunicipios.find(
    (item) => item.sigla === estado,
  );

  const municipiosDisponiveis = estadoSelecionado?.municipios || [];

  useEffect(() => {
    const checkMantenedoraStatus = async () => {
      try {
        const response = await axios.get(api.verify(), {
          withCredentials: true,
        });

        setMantenedoraRegistrada(response.data.isRegistered === true);

        if (response.data.isRegistered) {
          setMantenedoraRegistrada(true);
          setShowRegistro(false);
          setTimeout(() => navigate("/novoProcesso"), 0);
        } else {
          setShowRegistro(true);
        }
      } catch (error) {
        console.error("Erro ao verificar status da mantenedora:", error);

        setSnackbarMessage("Erro ao verificar status da mantenedora");
        setSnackbarOpen(true);
        navigate("/login");
      }
    };

    checkMantenedoraStatus();
  }, [navigate]);

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) setDocumento(file);
  };

  const handleCnpjChange = (e) => setCnpj(maskCNPJ(e.target.value));
  const handlePhoneChange = (e) => setTelefone(maskPhone(e.target.value));
  const handleCepChange = (e) => setCep(maskCEP(e.target.value));

  const handleSubmit = async (event) => {
    event.preventDefault();

    const cnpjSemMascara = cnpj.replace(/\D/g, "");
    const telefoneSemMascara = telefone.replace(/\D/g, "");
    const cepSemMascara = cep.replace(/\D/g, "");

    const formData = new FormData();

    formData.append("nomeMantenedora", nomeMantenedora);
    formData.append("cnpj", cnpjSemMascara);
    formData.append("rua", rua);
    formData.append("bairro", bairro);
    formData.append("numero", numero);
    formData.append("cidade", cidade);
    formData.append("estado", estado);
    formData.append("cep", cepSemMascara);
    formData.append("email", email);
    formData.append("telefone", telefoneSemMascara);

    if (documento) formData.append("documento", documento);

    try {
      await axios.post(api.registerMaintainer(), formData, {
        headers: { "Content-Type": "multipart/form-data" },
        withCredentials: true,
      });

      setMantenedoraRegistrada(true);
      setSnackbarMessage("Mantenedora registrada com sucesso!");
      setSnackbarOpen(true);
      onNext();
      navigate("/home/processos");
    } catch (error) {
      console.error("Erro ao registrar mantenedora:", error);

      const errorMessage =
        error.response?.data?.message || "Erro ao registrar mantenedora";

      setSnackbarMessage(errorMessage);
      setSnackbarOpen(true);
    }
  };

  const handleSnackbarClose = () => setSnackbarOpen(false);

  return (
    <Container
      component="div"
      sx={{
        py: 4,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "100vh",
      }}
    >
      {showRegistro && (
        <Card
          component="div"
          sx={{
            width: "100%",
            maxWidth: "800px",
            borderRadius: 4,
            boxShadow: "0px 4px 20px rgba(0,0,0,0.1)",
            overflow: "hidden",
          }}
        >
          <CardContent>
            <Typography
              variant="h4"
              component="div"
              sx={{
                mb: 4,
                fontWeight: "bold",
                color: "primary.main",
              }}
            >
              Dados da Mantenedora
            </Typography>

            <form onSubmit={handleSubmit}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Nome da Mantenedora"
                    fullWidth
                    value={nomeMantenedora}
                    onChange={(e) => setNomeMantenedora(e.target.value)}
                    required
                    component="div"
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    label="CNPJ"
                    fullWidth
                    value={cnpj}
                    onChange={handleCnpjChange}
                    required
                    placeholder="00.000.000/0000-00"
                    inputProps={{ maxLength: 18 }}
                    component="div"
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    label="Rua"
                    fullWidth
                    value={rua}
                    onChange={(e) => setRua(e.target.value)}
                    required
                    component="div"
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    label="Bairro"
                    fullWidth
                    value={bairro}
                    onChange={(e) => setBairro(e.target.value)}
                    required
                    component="div"
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    label="Número"
                    fullWidth
                    value={numero}
                    onChange={(e) => setNumero(e.target.value)}
                    required
                    component="div"
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    select
                    label="Estado (UF)"
                    fullWidth
                    value={estado}
                    onChange={(e) => {
                      setEstado(e.target.value);
                      setCidade("");
                    }}
                    required
                    component="div"
                  >
                    {estadosMunicipios.map((estadoItem) => (
                      <MenuItem key={estadoItem.id} value={estadoItem.sigla}>
                        {estadoItem.nome} ({estadoItem.sigla})
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    select
                    label="Cidade"
                    fullWidth
                    value={cidade}
                    onChange={(e) => setCidade(e.target.value)}
                    disabled={!estado}
                    required
                    component="div"
                  >
                    {municipiosDisponiveis.map((municipioItem) => (
                      <MenuItem
                        key={municipioItem.id}
                        value={municipioItem.nome}
                      >
                        {municipioItem.nome}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    label="CEP"
                    fullWidth
                    value={cep}
                    onChange={handleCepChange}
                    required
                    placeholder="00000-000"
                    inputProps={{ maxLength: 9 }}
                    component="div"
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    label="E-mail"
                    fullWidth
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    type="email"
                    component="div"
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    label="Telefone"
                    fullWidth
                    value={telefone}
                    onChange={handlePhoneChange}
                    required
                    placeholder="(00) 00000-0000"
                    inputProps={{ maxLength: 15 }}
                    component="div"
                  />
                </Grid>

                <Grid item xs={12}>
                  <Typography
                    variant="body1"
                    sx={{ mb: 1, fontWeight: "bold" }}
                  >
                    Anexe um documento para comprovação:
                  </Typography>

                  <Box sx={{ mt: 1, textAlign: "center" }}>
                    <input
                      accept="application/pdf"
                      id="documento"
                      type="file"
                      onChange={handleFileChange}
                      style={{ display: "none" }}
                    />

                    <label htmlFor="documento">
                      <Button
                        variant="outlined"
                        fullWidth
                        sx={{ py: 1.5 }}
                        component={motion.div}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        {documento
                          ? "Arquivo Anexado"
                          : "Anexar Documento (PDF)"}
                      </Button>
                    </label>

                    {documento && (
                      <div
                        style={{
                          marginTop: 10,
                          fontSize: ajustarTamanhoFonteEscola("0.875rem"),
                          fontWeight: "bold",
                          color: "green",
                        }}
                      >
                        {documento.name}
                      </div>
                    )}
                  </Box>

                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 1 }}
                  >
                    Anexe o{" "}
                    <strong>
                      Comprovante de Inscrição e de Situação Cadastral do CNPJ
                    </strong>
                    , emitido pela Receita Federal.
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <motion.div
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.98 }}
                    style={{ width: "100%" }}
                  >
                    <Button
                      type="submit"
                      variant="contained"
                      fullWidth
                      sx={{ py: 1.5, mt: 2 }}
                    >
                      Registrar Mantenedora
                    </Button>
                  </motion.div>
                </Grid>
              </Grid>
            </form>
          </CardContent>
        </Card>
      )}

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        component="div"
      >
        <Alert
          onClose={handleSnackbarClose}
          severity="info"
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
}
